import { useEffect, useState } from "react";
import { supabase } from "../lib/supabase";
import { fmtK } from "../lib/money";
import { KapioLogo } from "../components/KapioLogo";

// Page-local color tokens. Mirrors shared/theme.js where they overlap, plus
// the lime accent (#C0DD97 / #173404) which is specific to this marketing
// surface and not yet in the shared palette.
const C = {
  bg: "#FAFAF7",
  cream: "#F5F3EE",
  dust: "#DAD7CD",
  dustDk: "#C4C1B7",
  hunter: "#3A5A40",
  hunterDk: "#2D4732",
  lime: "#C0DD97",
  limeDk: "#173404",
  charcoal: "#1C1C1C",
  slate: "#4A4A4A",
  muted: "#7A7A6C",
  white: "#FFFFFF",
};

const CATEGORY_LABELS = ["Fashion", "Food", "Electronics", "Beauty", "Home", "Books"];
const PRODUCT_COLS =
  "id, title, price, images, seller_id, created_at, sellers(store_name, address, rating_avg, rating_count), category:categories(id, name, slug)";
const REQUEST_TIMEOUT_MS = 10_000;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function withTimeout(promise, ms) {
  return Promise.race([
    promise,
    new Promise((_, reject) => setTimeout(() => reject(new Error("timeout")), ms)),
  ]);
}

export function Home({ onSignIn, onBrowseStores, onViewStore, onBecomeSeller }) {
  const [searchQuery, setSearchQuery] = useState("");
  const [activeCategory, setActiveCategory] = useState("Fashion");
  const [waitlistOpen, setWaitlistOpen] = useState(false);

  const [featuredProduct, setFeaturedProduct] = useState(null);
  const [droppedProduct, setDroppedProduct] = useState(null);
  const [activeCategoryNames, setActiveCategoryNames] = useState(() => new Set());
  const [waitlistCount, setWaitlistCount] = useState(0);

  const [modalEmail, setModalEmail] = useState("");
  const [modalRole, setModalRole] = useState("");
  const [modalSubmitting, setModalSubmitting] = useState(false);
  const [modalError, setModalError] = useState("");
  const [modalSubmitted, setModalSubmitted] = useState(false);
  const [modalSubmittedEmail, setModalSubmittedEmail] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // get_top_selling_products is SECURITY DEFINER and ranks by all-time
        // SUM(order_items.quantity) excluding cancelled sub-orders — that's
        // the historically-most-bought signal we want for the hero.
        const [productsRes, catsRes, waitRes, topSellerRes] = await Promise.all([
          supabase
            .from("products")
            .select(PRODUCT_COLS)
            .eq("status", "active")
            .is("deleted_at", null)
            .order("created_at", { ascending: false })
            .limit(2),
          supabase
            .from("products")
            .select("category:categories(name)")
            .eq("status", "active")
            .is("deleted_at", null),
          supabase.from("waitlist").select("*", { count: "exact", head: true }),
          supabase.rpc("get_top_selling_products", { limit_n: 1, category_filter: null }),
        ]);
        if (cancelled) return;

        const recentProducts = productsRes.data || [];
        const topSellerId = topSellerRes.data?.[0]?.id;

        let featured = null;
        if (topSellerId) {
          const matchInRecent = recentProducts.find((p) => p.id === topSellerId);
          if (matchInRecent) {
            featured = matchInRecent;
          } else {
            const { data: topSellerProduct } = await supabase
              .from("products")
              .select(PRODUCT_COLS)
              .eq("id", topSellerId)
              .maybeSingle();
            if (cancelled) return;
            featured = topSellerProduct ?? null;
          }
        }
        if (!featured) featured = recentProducts[0] ?? null;

        const dropped = recentProducts.find((p) => p.id !== featured?.id) ?? null;

        setFeaturedProduct(featured);
        setDroppedProduct(dropped);

        const names = new Set(
          (catsRes.data || [])
            .map((r) => r.category?.name)
            .filter(Boolean)
        );
        setActiveCategoryNames(names);

        const featuredCatName = featured?.category?.name;
        if (featuredCatName && CATEGORY_LABELS.includes(featuredCatName)) {
          setActiveCategory(featuredCatName);
        } else {
          const firstActive = CATEGORY_LABELS.find((label) => names.has(label));
          if (firstActive) setActiveCategory(firstActive);
        }

        if (typeof waitRes.count === "number") setWaitlistCount(waitRes.count);
      } catch (err) {
        console.warn("[home] data load failed", err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!waitlistOpen) return;
    const onKey = (e) => {
      if (e.key === "Escape") closeModal();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [waitlistOpen]);

  function closeModal() {
    setWaitlistOpen(false);
    setTimeout(() => {
      setModalSubmitted(false);
      setModalError("");
      setModalEmail("");
      setModalRole("");
      setModalSubmittedEmail("");
    }, 200);
  }

  async function submitWaitlist() {
    if (modalSubmitting) return;
    if (!modalRole) {
      setModalError("Pick a role first.");
      return;
    }
    const trimmed = modalEmail.trim().toLowerCase();
    if (!EMAIL_RE.test(trimmed)) {
      setModalError("Please enter a valid email address.");
      return;
    }
    setModalError("");
    setModalSubmitting(true);
    try {
      const { error: err } = await withTimeout(
        supabase
          .from("waitlist")
          .insert({ email: trimmed, role: modalRole, source: "desktop-home-modal" }),
        REQUEST_TIMEOUT_MS,
      );
      if (err) {
        if (err.code === "23505") {
          setModalError("You're already on the list! We'll email you at launch.");
        } else {
          console.error("[home/waitlist] insert failed", err);
          setModalError(`Something went wrong: ${err.message}`);
        }
        return;
      }
      setModalSubmittedEmail(trimmed);
      setWaitlistCount((prev) => prev + 1);
      setModalEmail("");
      setModalRole("");
      setModalSubmitted(true);
    } catch (e) {
      console.error("[home/waitlist] submit threw", e);
      setModalError(
        e.message === "timeout"
          ? "Request timed out. Check your connection and try again."
          : `Error: ${e.message}`,
      );
    } finally {
      setModalSubmitting(false);
    }
  }

  const featuredStore = featuredProduct?.sellers?.store_name;
  const featuredAddress = featuredProduct?.sellers?.address;
  const featuredRatingAvg = featuredProduct?.sellers?.rating_avg;
  const featuredRatingCount = featuredProduct?.sellers?.rating_count;
  const featuredPrice =
    typeof featuredProduct?.price === "number" ? fmtK(featuredProduct.price) : null;

  const droppedStore = droppedProduct?.sellers?.store_name;
  const droppedPrice =
    typeof droppedProduct?.price === "number" ? fmtK(droppedProduct.price) : null;

  // Floor to nearest 10 once we cross 10 (e.g. 23 → "20+", 134 → "130+").
  // Below 10 show the literal count so we don't overstate at the very start.
  const waitlistDisplay =
    waitlistCount >= 10 ? String(Math.floor(waitlistCount / 10) * 10) : String(Math.max(0, waitlistCount));

  return (
    <div style={{
      minHeight: "100vh",
      background: "linear-gradient(180deg, #F5F3EE 0%, #E8E5DA 60%, #DAD7CD 100%)",
      color: C.charcoal,
    }}>
      <style>{`
        @keyframes kapioHomePulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.6; transform: scale(0.85); }
        }
        .live-dot {
          width: 6px; height: 6px; border-radius: 50%;
          background: ${C.lime}; animation: kapioHomePulse 2s ease-in-out infinite;
          display: inline-block;
        }
        .pill-cta:hover { background: #B0CD87 !important; transform: translateY(-1px); }
        .btn-primary-dark:hover { background: #000 !important; }
        .btn-glass:hover { background: ${C.white} !important; }
        .signin-btn:hover { background: ${C.hunterDk} !important; }
        .btn-cream:hover { background: ${C.white} !important; }
        .pill-dashed:hover {
          background: ${C.hunter} !important; color: ${C.bg} !important;
          border-color: ${C.hunter} !important;
        }
        .icon-btn:hover { background: ${C.white} !important; color: ${C.charcoal} !important; }
        .cat-pill:hover {
          background: ${C.white} !important;
          border-color: rgba(28,28,28,0.12) !important;
        }
        .cat-pill-disabled:hover {
          background: ${C.bg} !important;
          border-color: rgba(28,28,28,0.06) !important;
        }
        .product-mini:hover { transform: translateX(2px); }
        .social-ic:hover { background: ${C.white} !important; color: ${C.charcoal} !important; }
        .nav-search input::placeholder { color: ${C.dustDk}; }

        .home-modal-overlay {
          position: fixed; inset: 0;
          background: rgba(28, 28, 28, 0.55);
          z-index: 100;
          display: flex; align-items: center; justify-content: center;
          padding: 20px;
          backdrop-filter: blur(4px); -webkit-backdrop-filter: blur(4px);
        }
        .home-modal-dialog {
          background: ${C.bg};
          border-radius: 16px;
          padding: 36px 32px 32px;
          max-width: 440px; width: 100%;
          position: relative;
          box-shadow: 0 24px 64px rgba(0,0,0,0.25);
        }
        .home-modal-close {
          position: absolute; top: 14px; right: 14px;
          background: none; border: none;
          font-size: 26px; color: ${C.muted};
          cursor: pointer; line-height: 1;
          padding: 4px 10px; border-radius: 8px;
          font-family: inherit;
        }
        .home-modal-close:hover { color: ${C.charcoal}; background: ${C.cream}; }
        .home-modal-input {
          width: 100%;
          padding: 12px 14px;
          border: 1px solid rgba(28,28,28,0.12);
          border-radius: 9px;
          font-size: 14px;
          font-family: inherit;
          margin-bottom: 8px;
          background: ${C.bg};
          color: ${C.charcoal};
          box-sizing: border-box;
        }
        .home-modal-input:focus { outline: none; border-color: ${C.hunter}; }
        .home-modal-submit {
          width: 100%;
          padding: 12px;
          border-radius: 9px;
          background: ${C.hunter};
          color: ${C.bg};
          border: none;
          font-size: 14px;
          font-weight: 700;
          cursor: pointer;
          font-family: inherit;
          margin-top: 8px;
          transition: background 0.15s ease;
        }
        .home-modal-submit:hover { background: ${C.hunterDk}; }
        .home-modal-submit:disabled { opacity: 0.6; cursor: default; }

        @media (max-width: 1100px) {
          .bento { grid-template-columns: 1fr 1fr !important; }
          .cell-brand { grid-column: 1 !important; grid-row: 1 !important; }
          .cell-hero { grid-column: 2 !important; grid-row: 1 / 3 !important; min-height: 540px !important; }
          .cell-cats { grid-column: 1 !important; grid-row: 2 !important; }
          .cell-social { grid-column: 1 !important; grid-row: 3 !important; }
          .cell-seller { grid-column: 2 !important; grid-row: 3 !important; }
        }
        @media (max-width: 768px) {
          .page { padding: 12px !important; }
          .nav-glass { padding: 10px 14px !important; flex-wrap: wrap; gap: 8px; }
          .nav-search { display: none !important; }
          .bento { grid-template-columns: 1fr !important; gap: 8px !important; }
          .cell { padding: 20px !important; min-height: 0 !important; }
          .cell-hero { grid-column: 1 !important; grid-row: auto !important; min-height: 420px !important; }
          .cell-brand, .cell-cats, .cell-social, .cell-seller {
            grid-column: 1 !important; grid-row: auto !important;
          }
          .stat-big { font-size: 44px !important; letter-spacing: -1.8px !important; }
        }
        @media (prefers-reduced-motion: reduce) {
          .live-dot, .pill-cta { animation: none !important; transition: none !important; }
        }
      `}</style>

      <div className="page" style={{ maxWidth: 1280, margin: "0 auto", padding: "16px 20px 32px" }}>

        <nav className="nav-glass" style={{
          background: "rgba(250, 250, 247, 0.55)",
          borderRadius: 18, padding: "12px 20px",
          display: "flex", justifyContent: "space-between", alignItems: "center",
          marginBottom: 14,
          backdropFilter: "blur(14px)", WebkitBackdropFilter: "blur(14px)",
          border: "1px solid rgba(255, 255, 255, 0.5)",
          boxShadow: "0 1px 0 rgba(255,255,255,0.6) inset",
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 18 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
              <KapioLogo size={28} />
              <span style={{ fontSize: 16, fontWeight: 800, letterSpacing: "-0.3px", color: C.charcoal }}>Kapio</span>
            </div>
            <div className="nav-search" style={{
              background: "rgba(255, 255, 255, 0.7)", borderRadius: 22,
              padding: "9px 18px", display: "flex", alignItems: "center", gap: 8,
              minWidth: 320, border: "1px solid rgba(255, 255, 255, 0.4)",
            }}>
              <span style={{ color: C.muted, fontSize: 14 }}>⌕</span>
              <input
                type="text"
                placeholder="Search products, stores..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                style={{
                  border: "none", background: "transparent", outline: "none", flex: 1,
                  fontFamily: "inherit", fontSize: 13, color: C.charcoal,
                }}
              />
            </div>
          </div>
          <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
            <button className="icon-btn" aria-label="Wishlist" onClick={onSignIn} style={iconBtnStyle}>♡</button>
            <button className="icon-btn" aria-label="Cart" onClick={onSignIn} style={iconBtnStyle}>⛒</button>
            <button className="signin-btn" onClick={onSignIn} style={{
              background: C.hunter, color: C.dust, padding: "9px 20px",
              borderRadius: 22, fontSize: 13, fontWeight: 700,
              border: "none", cursor: "pointer", fontFamily: "inherit",
              transition: "background 0.15s ease",
            }}>Sign in</button>
          </div>
        </nav>

        <div className="bento" style={{
          display: "grid",
          gridTemplateColumns: "1fr 1.4fr 1fr",
          gridTemplateRows: "auto auto",
          gap: 10,
        }}>

          <div className="cell cell-brand" style={{
            ...glassCellStyle,
            gridColumn: 1, gridRow: 1,
            minHeight: 320,
            display: "flex", flexDirection: "column", justifyContent: "space-between",
          }}>
            <div>
              <div style={{
                background: C.charcoal, color: C.bg,
                padding: "5px 12px", borderRadius: 14,
                fontSize: 10, fontWeight: 600,
                display: "inline-flex", alignItems: "center", gap: 5,
                marginBottom: 14, letterSpacing: 0.2, width: "fit-content",
              }}>
                <span className="live-dot"></span>
                LIVE NOW IN ZAMBIA
              </div>
              <h1 style={{
                fontSize: "clamp(28px, 3.5vw, 42px)",
                fontWeight: 900, letterSpacing: "-1.5px",
                lineHeight: 0.95, color: C.charcoal, marginBottom: 10,
              }}>
                <span style={{ display: "block" }}>Shop local.</span>
                <span style={{ display: "block" }}>Sell smart.</span>
              </h1>
              <p style={{
                fontSize: 13, color: C.muted,
                lineHeight: 1.6, marginBottom: 18,
              }}>Zambia's marketplace for everyone. Real sellers, mobile money, escrow on every order.</p>
            </div>
            <button className="pill-cta" onClick={onBrowseStores} style={{
              background: C.lime, color: C.limeDk, border: "none",
              padding: "11px 22px", borderRadius: 22,
              fontSize: 13, fontWeight: 700, cursor: "pointer", fontFamily: "inherit",
              width: "fit-content", display: "inline-flex", alignItems: "center", gap: 6,
              transition: "all 0.15s ease",
            }}>
              Browse all stores <span style={{ fontSize: 14 }}>→</span>
            </button>
          </div>

          <div className="cell cell-hero" style={{
            gridColumn: 2, gridRow: "1 / 3",
            background: "linear-gradient(135deg, #DAD7CD 0%, #E8E5DA 50%, #C4C1B7 100%)",
            minHeight: 660, padding: 24,
            position: "relative", overflow: "hidden",
            borderRadius: 18,
            border: "1px solid rgba(255, 255, 255, 0.5)",
            boxShadow: "0 1px 0 rgba(255,255,255,0.6) inset",
            display: "flex", flexDirection: "column", justifyContent: "space-between",
          }}>
            <div style={{
              background: "rgba(28, 28, 28, 0.85)", color: C.bg,
              padding: "5px 12px", borderRadius: 14,
              fontSize: 10, fontWeight: 600,
              display: "inline-block", width: "fit-content",
              letterSpacing: 0.5,
            }}>
              {featuredProduct ? "★ FEATURED PRODUCT" : "★ FEATURED STORE COMING SOON"}
            </div>

            <div style={{
              position: "absolute",
              top: "50%", left: "50%",
              transform: "translate(-50%, -50%)",
              width: "70%", aspectRatio: "1",
              borderRadius: "50%",
              background: "radial-gradient(circle at 35% 35%, #E84855 0%, #C4243B 30%, #2D2D2D 80%, #1C1C1C 100%)",
              boxShadow: "0 30px 80px rgba(196, 36, 59, 0.2)",
            }} />

            <div style={{ position: "relative", zIndex: 2, display: "flex", flexDirection: "column", gap: 8 }}>
              {featuredStore && (
                <p style={{
                  margin: 0,
                  fontSize: 10, color: C.muted,
                  letterSpacing: 1.2, fontWeight: 600,
                  marginBottom: 4, textTransform: "uppercase",
                }}>
                  {`From ${featuredStore}${featuredAddress ? ` · ${featuredAddress}` : ""}`}
                </p>
              )}
              <h2 style={{
                margin: 0,
                fontSize: "clamp(28px, 3.2vw, 40px)",
                fontWeight: 900, letterSpacing: "-1.2px",
                color: C.charcoal, lineHeight: 1,
              }}>{featuredProduct?.title || "Our first products are landing soon."}</h2>
              <p style={{ margin: 0, fontSize: 13, color: C.charcoal, lineHeight: 1.5 }}>
                {featuredProduct
                  ? `${featuredPrice} · Free pickup at ZCAS${
                      typeof featuredRatingAvg === "number" && featuredRatingCount > 0
                        ? ` · ★ ${featuredRatingAvg.toFixed(1)} (${featuredRatingCount} review${featuredRatingCount === 1 ? "" : "s"})`
                        : ""
                    }`
                  : "Real sellers, escrow protection, mobile money — going live soon."}
              </p>
              <div style={{ display: "flex", gap: 8, marginTop: 14 }}>
                <button className="btn-primary-dark" onClick={onSignIn} style={{
                  background: C.charcoal, color: C.bg, border: "none",
                  padding: "11px 18px", borderRadius: 20,
                  fontSize: 12, fontWeight: 700, cursor: "pointer", fontFamily: "inherit",
                  transition: "background 0.15s ease",
                }}>{featuredProduct ? "Sign in to buy" : "Sign in"}</button>
                <button className="btn-glass" onClick={onViewStore} style={{
                  background: "rgba(255, 255, 255, 0.7)", color: C.charcoal,
                  border: "1px solid rgba(255, 255, 255, 0.4)",
                  padding: "11px 18px", borderRadius: 20,
                  fontSize: 12, fontWeight: 700, cursor: "pointer", fontFamily: "inherit",
                  backdropFilter: "blur(8px)",
                  transition: "background 0.15s ease",
                }}>{featuredProduct ? "View store" : "Browse stores"}</button>
              </div>
            </div>
          </div>

          <div className="cell cell-cats" style={{
            ...glassLightCellStyle,
            gridColumn: 3, gridRow: 1,
            minHeight: 320, padding: 22,
            display: "flex", flexDirection: "column", justifyContent: "space-between",
          }}>
            <div>
              <p style={eyebrowStyle}>SHOP BY CATEGORY</p>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 18 }}>
                {CATEGORY_LABELS.map((cat) => {
                  const isActive = activeCategory === cat;
                  const hasProducts = activeCategoryNames.has(cat);
                  return (
                    <span
                      key={cat}
                      className={hasProducts ? "cat-pill" : "cat-pill-disabled"}
                      onClick={hasProducts ? () => setActiveCategory(cat) : undefined}
                      style={{
                        background: isActive ? C.hunter : C.bg,
                        color: isActive ? C.dust : C.charcoal,
                        padding: "6px 13px", borderRadius: 14,
                        fontSize: 12,
                        fontWeight: isActive ? 700 : 500,
                        border: isActive
                          ? `1px solid ${C.hunter}`
                          : "1px solid rgba(28, 28, 28, 0.06)",
                        cursor: hasProducts ? "pointer" : "default",
                        opacity: hasProducts ? 1 : 0.45,
                        transition: "all 0.15s ease",
                      }}
                    >{cat}</span>
                  );
                })}
              </div>
            </div>
            <div style={{
              borderTop: "1px solid rgba(28, 28, 28, 0.08)",
              paddingTop: 14,
            }}>
              <p style={{ ...eyebrowStyle, marginBottom: 10 }}>JUST DROPPED</p>
              {droppedProduct ? (
                <div className="product-mini" onClick={onSignIn} style={{
                  display: "flex", gap: 10, alignItems: "center",
                  cursor: "pointer", transition: "transform 0.15s ease",
                }}>
                  <div style={{
                    width: 44, height: 44, borderRadius: 9,
                    background: "linear-gradient(135deg, #6F6F6F, #4F4F4F)",
                    flexShrink: 0,
                  }} />
                  <div>
                    <p style={{ fontSize: 13, fontWeight: 700, color: C.charcoal, margin: 0 }}>{droppedProduct.title}</p>
                    <p style={{ fontSize: 11, color: C.muted, marginTop: 2, margin: 0 }}>
                      {`${droppedStore || "Kapio store"}${droppedPrice ? ` · ${droppedPrice}` : ""}`}
                    </p>
                  </div>
                </div>
              ) : (
                <p style={{ fontSize: 12, color: C.muted, lineHeight: 1.55, margin: 0 }}>
                  More products land here as sellers join.
                </p>
              )}
            </div>
          </div>

          <div className="cell cell-social" style={{
            ...glassCellStyle,
            gridColumn: 1, gridRow: 2,
            minHeight: 320,
            display: "flex", flexDirection: "column", justifyContent: "space-between",
          }}>
            <div>
              <p style={eyebrowStyle}>JOINING US</p>
              <p className="stat-big" style={{
                fontSize: 56, fontWeight: 900,
                color: C.hunter, letterSpacing: "-2.5px",
                lineHeight: 0.9, margin: 0,
              }}>{waitlistDisplay}<span style={{ fontSize: "0.6em", opacity: 0.6 }}>+</span></p>
              <p style={{ fontSize: 14, fontWeight: 700, color: C.charcoal, margin: "6px 0 8px" }}>
                on the waitlist
              </p>
              <p style={{ fontSize: 12, color: C.muted, lineHeight: 1.55 }}>
                Zambian shoppers and sellers waiting for Kapio to go live in Q2 2026.
              </p>
            </div>
            <button
              className="pill-dashed"
              onClick={() => setWaitlistOpen(true)}
              style={{
                background: "transparent", color: C.hunter,
                border: "1px dashed rgba(58, 90, 64, 0.4)",
                padding: "9px 16px", borderRadius: 18,
                fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: "inherit",
                transition: "all 0.15s ease", width: "fit-content",
              }}
            >+ Join the waitlist</button>
          </div>

          <div className="cell cell-seller" style={{
            gridColumn: 3, gridRow: 2,
            background: C.hunter, color: C.bg,
            minHeight: 320, padding: 24,
            display: "flex", flexDirection: "column", justifyContent: "space-between",
            borderRadius: 18,
            boxShadow: "0 1px 0 rgba(255,255,255,0.06) inset",
          }}>
            <div>
              <p style={{
                fontSize: 10, color: C.dust,
                letterSpacing: 1.2, fontWeight: 600,
                opacity: 0.85, marginBottom: 14,
              }}>FOR SELLERS</p>
              <h3 style={{
                fontSize: 22, fontWeight: 900,
                letterSpacing: "-0.6px", lineHeight: 1.1,
                marginBottom: 8,
              }}>Start your store in 5 minutes.</h3>
              <p style={{
                fontSize: 13, color: C.dust, opacity: 0.85,
                lineHeight: 1.55, marginBottom: 16,
              }}>10% commission. Free to list. Get paid via Airtel Money or MTN MoMo. 48-hour escrow protection on every order.</p>
            </div>
            <button className="btn-cream" onClick={onBecomeSeller} style={{
              background: C.bg, color: C.charcoal, border: "none",
              padding: "11px 20px", borderRadius: 20,
              fontSize: 13, fontWeight: 700, cursor: "pointer", fontFamily: "inherit",
              width: "fit-content", transition: "background 0.15s ease",
            }}>Open a store →</button>
          </div>

        </div>

        <div style={{
          display: "flex", justifyContent: "space-between", alignItems: "center",
          marginTop: 16, padding: "0 8px",
        }}>
          <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
            <span style={{ fontSize: 11, color: C.muted }}>Follow Kapio</span>
            <div style={{ display: "flex", gap: 6 }}>
              {["𝕏", "◉", "▶"].map((icon, i) => (
                <div key={i} className="social-ic" style={{
                  width: 26, height: 26, borderRadius: "50%",
                  background: "rgba(255, 255, 255, 0.6)",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  fontSize: 11, color: C.muted,
                  cursor: "pointer", transition: "background 0.15s ease",
                }}>{icon}</div>
              ))}
            </div>
          </div>
          <p style={{ fontSize: 11, color: C.muted }}>Built in Zambia 🇿🇲</p>
        </div>

      </div>

      {waitlistOpen && (
        <div
          className="home-modal-overlay"
          role="dialog"
          aria-modal="true"
          aria-labelledby="home-waitlist-title"
          onClick={(e) => {
            if (e.currentTarget === e.target) closeModal();
          }}
        >
          <div className="home-modal-dialog">
            <button className="home-modal-close" onClick={closeModal} aria-label="Close">×</button>
            {!modalSubmitted ? (
              <>
                <h3 id="home-waitlist-title" style={{ fontSize: 22, fontWeight: 800, color: C.charcoal, margin: "0 0 6px", letterSpacing: "-0.015em" }}>
                  Join the waitlist
                </h3>
                <p style={{ fontSize: 14, color: C.muted, margin: "0 0 20px", lineHeight: 1.55 }}>
                  We'll email you the moment Kapio goes live in Zambia.
                </p>
                <div style={{ display: "flex", gap: 8, marginBottom: 10 }}>
                  {[{ r: "buyer", l: "I'll shop" }, { r: "seller", l: "I'll sell" }].map((o) => (
                    <button
                      key={o.r}
                      onClick={() => setModalRole(o.r)}
                      style={{
                        flex: 1,
                        padding: "14px 0",
                        borderRadius: 10,
                        border: `1.5px solid ${modalRole === o.r ? C.hunter : C.dustDk}`,
                        background: modalRole === o.r ? `${C.hunter}10` : C.white,
                        cursor: "pointer",
                        fontSize: 14,
                        fontWeight: 700,
                        color: modalRole === o.r ? C.hunter : C.slate,
                        fontFamily: "inherit",
                        minHeight: 48,
                        transition: "all 0.15s ease",
                      }}
                    >{o.l}</button>
                  ))}
                </div>
                <input
                  type="email"
                  placeholder="your@email.com"
                  className="home-modal-input"
                  value={modalEmail}
                  onChange={(e) => setModalEmail(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") submitWaitlist(); }}
                  autoFocus
                />
                <button
                  onClick={submitWaitlist}
                  disabled={modalSubmitting}
                  className="home-modal-submit"
                >
                  {modalSubmitting ? "Joining…" : "Join the waitlist →"}
                </button>
                {modalError && (
                  <p style={{ fontSize: 12, color: "#B91C1C", margin: "8px 0 0", lineHeight: 1.4 }} role="alert">
                    {modalError}
                  </p>
                )}
                <p style={{ fontSize: 12, color: C.muted, margin: "14px 0 0", textAlign: "center" }}>100% free. No spam.</p>
              </>
            ) : (
              <>
                <p style={{ fontSize: 38, margin: "0 0 8px" }}>✓</p>
                <h3 style={{ fontSize: 22, fontWeight: 800, color: C.charcoal, margin: "0 0 6px", letterSpacing: "-0.015em" }}>
                  You're on the list.
                </h3>
                <p style={{ fontSize: 14, color: C.muted, margin: "0 0 20px", lineHeight: 1.55 }}>
                  We'll email <span style={{ fontWeight: 700, color: C.charcoal }}>{modalSubmittedEmail}</span> the moment we go live.
                </p>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

const glassCellStyle = {
  borderRadius: 18,
  padding: 24,
  border: "1px solid rgba(255, 255, 255, 0.5)",
  boxShadow: "0 1px 0 rgba(255,255,255,0.6) inset",
  background: "rgba(250, 250, 247, 0.55)",
  backdropFilter: "blur(14px)",
  WebkitBackdropFilter: "blur(14px)",
};

const glassLightCellStyle = {
  borderRadius: 18,
  padding: 24,
  border: "1px solid rgba(255, 255, 255, 0.5)",
  boxShadow: "0 1px 0 rgba(255,255,255,0.6) inset",
  background: "rgba(250, 250, 247, 0.7)",
  backdropFilter: "blur(10px)",
  WebkitBackdropFilter: "blur(10px)",
};

const eyebrowStyle = {
  fontSize: 10,
  color: "#7A7A6C",
  letterSpacing: 1.2,
  fontWeight: 600,
  marginBottom: 14,
};

const iconBtnStyle = {
  width: 36, height: 36, borderRadius: "50%",
  background: "rgba(250, 250, 247, 0.9)",
  border: "1px solid rgba(255, 255, 255, 0.4)",
  display: "flex", alignItems: "center", justifyContent: "center",
  cursor: "pointer", color: "#7A7A6C", fontSize: 14,
  transition: "all 0.15s ease",
};
